import { useEffect, useState } from 'react';
import { Button } from '@/ui/Button';
import { ConfirmDialog } from '@/ui/Dialog';
import { SkeletonTarjetas } from '@/ui/Skeleton';
import { EstadoError } from '@/components/EstadoError';
import { EstadoVacio } from '@/components/EstadoVacio';
import { DetalleDeEvento } from './DetalleDeEvento';
import { FormularioDeEvento } from './FormularioDeEvento';
import { ConmutadorDeVistas, TarjetaDeEvento } from './piezas';
import {
  useCancelarInscripcion, useEvento, useEventos, useInscribirse, useReactivar,
  type Evento, type EventoConGaleria,
} from './api';

/**
 * Eventos.tsx — la pantalla de `#eventos`. Módulo 8, opción 1.
 *
 * ## Tres puertas llevan a un evento concreto, y ahora llegan
 *
 * `#eventos/<id>` abre el detalle de ese evento. Hasta el 2026-09-17 no había
 * forma de abrir uno, así que tres sitios tiraban el que se había pulsado: la
 * búsqueda global (`"route": "events"`), el «Ver evento» del evento fijado en
 * el Inicio y cada fila de «Próximos eventos». Decidido con quien encargó el
 * trabajo, como la Biblioteca y el Directorio.
 *
 * ## Próximos y pasados, por separado
 *
 * El vanilla pedía los 30 primeros **desde el más antiguo**, así que los
 * eventos ya celebrados iban delante —el 2026-09-17, tres de junio y julio
 * antes que el de diciembre— y con 30 pasados los próximos dejaban de salir.
 * Ahora son dos consultas con `cuando`, y los pasados van del más reciente
 * hacia atrás, de a doce.
 *
 * ## La gestión pregunta el permiso
 *
 * `role !== 'colaborador'` en el vanilla; `require_permission("eventos",
 * "manage")` en el servidor. Fallaba en las dos direcciones.
 */

const POR_TANDA_DE_PASADOS = 12;
const LIMITE_DE_PROXIMOS = 60;

export function Eventos() {
  const puedeGestionar = Auth.can('eventos', 'manage');
  const [inactivos, setInactivos] = useState(false);
  const [limitePasados, setLimitePasados] = useState(POR_TANDA_DE_PASADOS);

  const proximos = useEventos('proximos', inactivos, LIMITE_DE_PROXIMOS);
  const pasados = useEventos('pasados', inactivos, limitePasados);

  const [viendo, setViendo] = useState<number | null>(null);
  const [editando, setEditando] = useState<EventoConGaleria | null>(null);
  const [creando, setCreando] = useState(false);
  const [cancelando, setCancelando] = useState<Evento | null>(null);

  const inscribirse = useInscribirse();
  const cancelar = useCancelarInscripcion();
  const reactivar = useReactivar();

  // La sub-ruta se lee UNA vez, al montar: leída en cada render, cerrar el
  // detalle lo volvería a abrir.
  const [pedido] = useState(() => {
    const p = window._routeParams?.[0];
    return p && /^\d+$/.test(p) ? Number(p) : null;
  });
  const comprobacion = useEvento(pedido);

  useEffect(() => {
    if (pedido == null) return;
    if (comprobacion.isSuccess) setViendo(pedido);
    // Un id que ya no está: el evento se desactivó o no existe. Se dice, y la
    // URL vuelve a la lista para que recargar no repita el aviso.
    if (comprobacion.isError) {
      Utils.toast('Ese evento ya no está disponible', 'warning');
      history.replaceState(null, '', '#eventos');
    }
  }, [pedido, comprobacion.isSuccess, comprobacion.isError]);

  function cerrarDetalle() {
    setViendo(null);
    if (location.hash !== '#eventos') history.replaceState(null, '', '#eventos');
  }

  function editar(e: EventoConGaleria) {
    setViendo(null);
    setEditando(e);
  }

  const aviso = (err: unknown, porDefecto: string) =>
    Utils.toast(err instanceof Error ? err.message : porDefecto, 'error');

  function hacerInscripcion(id: number) {
    inscribirse.mutate(id, {
      onSuccess: () => Utils.toast('Inscripción confirmada', 'success'),
      onError: (err) => aviso(err, 'No se pudo completar la inscripción'),
    });
  }

  function confirmarCancelacion() {
    if (!cancelando) return;
    cancelar.mutate(cancelando.id, {
      onSuccess: () => { setCancelando(null); Utils.toast('Inscripción cancelada', 'info'); },
      onError: (err) => { setCancelando(null); aviso(err, 'No se pudo cancelar la inscripción'); },
    });
  }

  function hacerReactivacion(id: number) {
    reactivar.mutate(id, {
      onSuccess: () => Utils.toast('Evento reactivado', 'success'),
      onError: (err) => aviso(err, 'No se pudo reactivar el evento'),
    });
  }

  const tarjetas = (lista: Evento[]) => (
    <ul className="grid gap-4 [grid-template-columns:repeat(auto-fill,minmax(268px,1fr))]">
      {lista.map((e) => (
        <li key={e.id} className="flex">
          <TarjetaDeEvento
            e={e}
            onVer={setViendo}
            onInscribirse={hacerInscripcion}
            onCancelar={setCancelando}
            onReactivar={puedeGestionar ? hacerReactivacion : undefined}
            ocupado={
              (inscribirse.isPending && inscribirse.variables === e.id) ||
              (reactivar.isPending && reactivar.variables === e.id)
            }
          />
        </li>
      ))}
    </ul>
  );

  return (
    <>
      <header className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-6 font-bold text-brand-ink">
            <i className="bi bi-calendar-event" aria-hidden="true" />
            Eventos
          </h1>
          <p className="mt-1 text-3 text-muted">Actividades y encuentros del equipo</p>
        </div>
        {puedeGestionar && (
          <div className="flex flex-wrap items-center gap-3">
            <label className="flex min-h-11 cursor-pointer items-center gap-2 text-2 text-ink-2">
              <input
                type="checkbox"
                checked={inactivos}
                onChange={(e) => setInactivos(e.target.checked)}
                className="size-4 accent-brand"
              />
              Mostrar inactivos
            </label>
            <Button onClick={() => setCreando(true)}>
              <i className="bi bi-plus-circle" aria-hidden="true" /> Nuevo evento
            </Button>
          </div>
        )}
      </header>

      <ConmutadorDeVistas activa="lista" />

      <section aria-labelledby="eventos-proximos" className="mb-8">
        <h2 id="eventos-proximos" className="mb-3 text-4 font-bold text-brand-ink">Próximos</h2>
        {proximos.isLoading ? (
          <SkeletonTarjetas n={3} />
        ) : proximos.isError ? (
          <EstadoError err={proximos.error} que="los eventos" onReintentar={() => void proximos.refetch()} />
        ) : !proximos.data?.length ? (
          <EstadoVacio
            icono="bi-calendar-x"
            titulo="No hay eventos próximos"
            descripcion={puedeGestionar
              ? 'Crea el siguiente para que el equipo pueda inscribirse.'
              : 'Cuando Bienestar programe uno, aparecerá aquí.'}
            accion={puedeGestionar ? { texto: 'Nuevo evento', onClick: () => setCreando(true) } : undefined}
            compacto
          />
        ) : (
          tarjetas(proximos.data)
        )}
      </section>

      <section aria-labelledby="eventos-pasados">
        <h2 id="eventos-pasados" className="mb-3 text-4 font-bold text-brand-ink">Pasados</h2>
        {pasados.isLoading ? (
          <SkeletonTarjetas n={3} />
        ) : pasados.isError ? (
          <EstadoError err={pasados.error} que="los eventos pasados" onReintentar={() => void pasados.refetch()} />
        ) : !pasados.data?.length ? (
          <p className="text-3 text-muted">Todavía no hay eventos celebrados.</p>
        ) : (
          <>
            {tarjetas(pasados.data)}
            {pasados.data.length >= limitePasados && (
              <div className="mt-4 flex justify-center">
                <Button
                  variant="secondary"
                  loading={pasados.isFetching}
                  onClick={() => setLimitePasados((n) => n + POR_TANDA_DE_PASADOS)}
                >
                  Ver más eventos pasados
                </Button>
              </div>
            )}
          </>
        )}
      </section>

      <DetalleDeEvento id={viendo} onCerrar={cerrarDetalle} onEditar={editar} />
      {puedeGestionar && (
        <FormularioDeEvento
          abierto={creando || editando != null}
          evento={editando}
          onCerrar={() => { setCreando(false); setEditando(null); }}
        />
      )}
      <ConfirmDialog
        abierto={cancelando != null}
        onCambio={(v) => { if (!v) setCancelando(null); }}
        titulo="Cancelar tu inscripción"
        descripcion={cancelando ? `Dejarás tu cupo en «${cancelando.title}». Puedes volver a inscribirte mientras queden cupos.` : ''}
        textoConfirmar="Cancelar inscripción"
        destructivo
        cargando={cancelar.isPending}
        onConfirmar={confirmarCancelacion}
      />
    </>
  );
}
